// External libraries
import { useMutation, UseMutationResult } from '@tanstack/react-query'

// Global utilities and constants
import { queryClient } from '@/App'
import { toast } from 'sonner'
import { documentKeys } from '@/hooks/documents/documentsQueryKeys'

// Services and API calls
import { addDocumentFromURL } from '@/api/documents'

// Hooks and Jotai Atoms
import { userAtom } from '@/store/store'
import { useAtomValue } from 'jotai'

// Types
import { CourseDocuments, BaseDocument } from '@/types/Document'

type AddDocumentFromURLParams = {
  url: string
  courseName: string
  fileName: string
  createdBy: string
  sections: string[]
}

/**
 * Custom hook for managing document creation from URLs with optimistic updates
 */
const useAddDocumentFromURL = () => {
  const { userId } = useAtomValue(userAtom)

  return useMutation({
    mutationFn: ({ url, courseName, fileName, createdBy, sections }: AddDocumentFromURLParams) =>
      addDocumentFromURL(url, courseName, fileName, createdBy, sections),

    onMutate: async (newDocumentParams) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: documentKeys.all,
      })

      // Snapshot previous values
      const previousDocs = queryClient.getQueryData<CourseDocuments>(documentKeys.byUser(userId)) || {}
      const previousCourseState = [...(previousDocs[newDocumentParams.courseName] || [])]

      // Create optimistic update with sourceUrl
      const optimisticDoc: BaseDocument = {
        fileName: newDocumentParams.fileName,
        createdBy: newDocumentParams.createdBy,
        isIndexed: false,
        sourceUrl: newDocumentParams.url,
      }

      // Create new state
      const newDocs = { ...previousDocs }
      const courseDocuments = [...(previousDocs[newDocumentParams.courseName] || []), optimisticDoc]
      newDocs[newDocumentParams.courseName] = courseDocuments

      // Update all cache levels immediately
      queryClient.setQueryData(documentKeys.byUser(userId), newDocs)
      queryClient.setQueryData(documentKeys.byCourse(userId, newDocumentParams.courseName), courseDocuments)
      queryClient.setQueryData(
        documentKeys.byDocument(userId, newDocumentParams.courseName, newDocumentParams.fileName),
        optimisticDoc,
      )

      return { previousDocs, previousCourseState }
    },

    onSuccess: (data, newDocumentParams) => {
      toast.success('Document added successfully!', {
        description: 'Web page has been added to the database: ' + newDocumentParams.fileName,
      })

      // Invalidate all affected queries to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: documentKeys.byUser(userId),
      })
    },

    onError: (error, newDocumentParams, context) => {
      // Rollback on error
      if (context) {
        queryClient.setQueryData(documentKeys.byUser(userId), context.previousDocs)
        queryClient.setQueryData(
          documentKeys.byCourse(userId, newDocumentParams.courseName),
          context.previousCourseState,
        )
      }

      toast.error("Couldn't add document from URL", {
        description: error instanceof Error ? error.message : 'Please try again',
      })
    },
  })
}

export default useAddDocumentFromURL
